/**
 * FamillesPage — gestion des familles/enfants par le staff + import CSV + inscription.
 *
 * Le staff (agent/manager) peut : créer une famille, importer un CSV, ouvrir une
 * famille pour voir ses enfants, et inscrire un enfant à la cantine / au périscolaire
 * sur une période (cas « familles sans numérique » — l'agent inscrit à la place du parent).
 */

import { useEffect, useState } from 'react';
import { Plus, Upload, Download, Users, ChevronRight, CalendarPlus, FileText, CalendarDays } from 'lucide-react';
import { api, ApiException } from '@/lib/api';
import { compressImageIfNeeded } from '@/lib/image-compress';
import { Card, CardBody, Button, Input, Field, Alert, Badge } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { ScolaireLayout } from '@/components/scolaire/ScolaireLayout';
import { useAuth } from '@/lib/auth-context';
import { PresenceCalendar } from '@/components/scolaire/PresenceCalendar';
import { FamillePiecesPanel } from '@/components/famille/FamillePiecesPanel';
import { FamilleResponsablesPanel } from '@/components/famille/FamilleResponsablesPanel';
import type {
  ScolaireFamille, ScolaireFamilleDetail, ScolaireEnfant, ScolaireImportResult, PeriscolaireCreneau,
  DossierScolaireDetail, DossierPieceDemandee, DossierPieceDeposee,
} from '@roronoa-collectivites/shared-types';

const JOURS = [
  { n: 1, label: 'Lun' }, { n: 2, label: 'Mar' }, { n: 3, label: 'Mer' },
  { n: 4, label: 'Jeu' }, { n: 5, label: 'Ven' },
];

export function FamillesPage() {
  const { user } = useAuth();
  const peutGerer = user ? ['super_admin', 'admin', 'manager'].includes(user.role) : false;
  const [familles, setFamilles] = useState<ScolaireFamille[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [familleDialog, setFamilleDialog] = useState(false);
  const [nomFamille, setNomFamille] = useState('');
  const [saving, setSaving] = useState(false);

  const [importDialog, setImportDialog] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [importResult, setImportResult] = useState<ScolaireImportResult | null>(null);
  const [importing, setImporting] = useState(false);
  const [exporting, setExporting] = useState(false);

  async function exporter(format: 'csv' | 'pdf') {
    setExporting(true); setError(null);
    try {
      const blob = await api.scolaire.exportEnfantsBlob(format);
      const today = new Date();
      const d = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `enfants-scolaire-${d}.${format}`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setError(e instanceof ApiException ? e.message : "L'export a échoué.");
    } finally {
      setExporting(false);
    }
  }

  // Détail famille ouvert
  const [detail, setDetail] = useState<ScolaireFamilleDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // Dialog inscription
  const [inscEnfant, setInscEnfant] = useState<ScolaireEnfant | null>(null);
  const [inscService, setInscService] = useState<'cantine' | 'periscolaire' | 'alsh'>('cantine');
  const [inscDebut, setInscDebut] = useState('');
  const [inscFin, setInscFin] = useState('');
  const [inscJours, setInscJours] = useState<number[]>([1, 2, 4, 5]);
  const [inscCreneaux, setInscCreneaux] = useState<PeriscolaireCreneau[]>(['matin']);
  const [inscAlshType, setInscAlshType] = useState<'mercredis' | 'vacances'>('mercredis');
  const [inscAlshFormule, setInscAlshFormule] = useState<'journee' | 'matin' | 'apres_midi'>('journee');
  const [presencesEnfant, setPresencesEnfant] = useState<string | null>(null);
  const [inscMsg, setInscMsg] = useState<string | null>(null);
  const [inscSaving, setInscSaving] = useState(false);

  async function load() {
    try { setFamilles(await api.scolaire.listFamilles()); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setLoading(false); }
  }
  useEffect(() => { void load(); }, []);

  async function openFamille(f: ScolaireFamille) {
    setDetailLoading(true); setError(null);
    try { setDetail(await api.scolaire.getFamille(f.id)); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur de chargement'); }
    finally { setDetailLoading(false); }
  }

  async function createFamille() {
    if (!nomFamille.trim()) return;
    setSaving(true); setError(null);
    try {
      await api.scolaire.createFamille({ nom: nomFamille.trim() });
      setFamilleDialog(false); setNomFamille('');
      await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de la création'); }
    finally { setSaving(false); }
  }

  async function runImport() {
    if (!csvText.trim()) return;
    setImporting(true); setError(null); setImportResult(null);
    try {
      const res = await api.scolaire.importCsv(csvText);
      setImportResult(res);
      await load();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors de l\'import'); }
    finally { setImporting(false); }
  }

  function ouvrirInscription(enfant: ScolaireEnfant) {
    setInscEnfant(enfant); setInscService('cantine');
    setInscDebut(''); setInscFin(''); setInscJours([1, 2, 4, 5]); setInscCreneaux(['matin']);
    setInscMsg(null);
  }

  async function inscrire() {
    if (!inscEnfant || !inscDebut || !inscFin) return;
    if (inscService !== 'alsh' && inscJours.length === 0) return;
    setInscSaving(true); setInscMsg(null);
    try {
      if (inscService === 'cantine') {
        const r = await api.scolaire.createInscription({ enfantId: inscEnfant.id, dateDebut: inscDebut, dateFin: inscFin, joursSemaine: inscJours });
        setInscMsg(`${r.reservationsCreees} repas réservé(s).`);
      } else if (inscService === 'periscolaire') {
        if (inscCreneaux.length === 0) { setInscMsg('Choisissez au moins un créneau.'); setInscSaving(false); return; }
        const r = await api.scolaire.periscolaire.createInscription({ enfantId: inscEnfant.id, dateDebut: inscDebut, dateFin: inscFin, joursSemaine: inscJours, creneaux: inscCreneaux });
        setInscMsg(`${r.reservationsCreees} créneau(x) réservé(s).`);
      } else {
        const r = await api.scolaire.alsh.createInscription({ enfantId: inscEnfant.id, dateDebut: inscDebut, dateFin: inscFin, typePeriode: inscAlshType, formule: inscAlshFormule });
        setInscMsg(`${r.reservationsCreees} journée(s) ALSH réservée(s).`);
      }
    } catch (e) { setInscMsg(e instanceof ApiException ? e.message : 'Erreur lors de l\'inscription'); }
    finally { setInscSaving(false); }
  }

  return (
    <ScolaireLayout activeTab="familles">
      <main className="mx-auto max-w-4xl px-4 py-6 sm:px-6">
        <div className="mb-6 flex items-center justify-between">
          <div>
            <h1 className="font-display text-2xl font-semibold tracking-r-tight text-ink">Familles &amp; enfants</h1>
            <p className="mt-1 text-sm text-ink-muted">{familles.length} famille{familles.length > 1 ? 's' : ''} enregistrée{familles.length > 1 ? 's' : ''}.</p>
          </div>
          {peutGerer && (
            <div className="flex flex-wrap gap-2">
              <Button variant="ghost" onClick={() => { setImportResult(null); setImportDialog(true); }}>
                <Upload className="h-4 w-4" aria-hidden="true" /> Import CSV
              </Button>
              <Button variant="ghost" onClick={() => void exporter('csv')} disabled={exporting}>
                <Download className="h-4 w-4" aria-hidden="true" /> Export CSV
              </Button>
              <Button variant="ghost" onClick={() => void exporter('pdf')} disabled={exporting}>
                <Download className="h-4 w-4" aria-hidden="true" /> Export PDF
              </Button>
              <Button variant="cantine" onClick={() => setFamilleDialog(true)}>
                <Plus className="h-4 w-4" aria-hidden="true" /> Nouvelle famille
              </Button>
            </div>
          )}
        </div>

        {error && <Alert variant="error" className="mb-4">{error}</Alert>}

        {loading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : familles.length === 0 ? (
          <Card><CardBody><p className="text-sm text-ink-muted">Aucune famille. Ajoutez-en une ou importez un CSV.</p></CardBody></Card>
        ) : (
          <ul className="space-y-2">
            {familles.map((f) => (
              <li key={f.id}>
                <button type="button" onClick={() => openFamille(f)} className="w-full text-left">
                  <Card>
                    <CardBody className="flex items-center justify-between py-3 transition-colors hover:bg-surface/40">
                      <div className="flex items-center gap-2">
                        <Users className="h-5 w-5 text-cantine-fg" aria-hidden="true" />
                        <span className="text-sm font-medium text-ink">{f.nom}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {f.ville && <Badge>{f.ville}</Badge>}
                        <ChevronRight className="h-4 w-4 text-ink-subtle" aria-hidden="true" />
                      </div>
                    </CardBody>
                  </Card>
                </button>
              </li>
            ))}
          </ul>
        )}
      </main>

      {/* Dialog création famille */}
      <Dialog open={familleDialog} onClose={() => setFamilleDialog(false)} title="Nouvelle famille"
        footer={<>
          <Button variant="ghost" onClick={() => setFamilleDialog(false)}>Annuler</Button>
          <Button variant="cantine" loading={saving} onClick={createFamille}>Créer</Button>
        </>}>
        <Field label="Nom de la famille" htmlFor="nom-famille" required>
          <Input id="nom-famille" value={nomFamille} onChange={(e) => setNomFamille(e.target.value)} placeholder="Martin" />
        </Field>
      </Dialog>

      {/* Dialog import CSV */}
      <Dialog open={importDialog} onClose={() => setImportDialog(false)} title="Import CSV des enfants" size="lg"
        footer={<>
          <Button variant="ghost" onClick={() => setImportDialog(false)}>Fermer</Button>
          <Button variant="cantine" loading={importing} onClick={runImport} disabled={!csvText.trim()}>Importer</Button>
        </>}>
        <div className="space-y-3">
          <p className="text-sm text-ink-muted">
            Collez un CSV avec l'en-tête : <code className="text-cantine-fg">nom_famille;prenom_enfant;nom_enfant;date_naissance;ecole;classe</code>.
            Les familles manquantes sont créées automatiquement. Date au format AAAA-MM-JJ (optionnelle).
          </p>
          <textarea value={csvText} onChange={(e) => setCsvText(e.target.value)} rows={8}
            placeholder={'nom_famille;prenom_enfant;nom_enfant;date_naissance\nMartin;Léo;Martin;2017-05-12'}
            className="w-full rounded-r-lg border border-border bg-surface/60 p-3 font-mono text-xs text-ink focus:outline-none focus:ring-2 focus:ring-cantine focus:ring-offset-2 focus:ring-offset-bg" />
          {importResult && (
            <div className="rounded-r-lg border border-border bg-surface/40 p-3">
              <div className="mb-2 flex flex-wrap gap-2 text-xs">
                <Badge>{importResult.enfantsCrees} enfant(s) créé(s)</Badge>
                <Badge>{importResult.famillesCreees} famille(s) créée(s)</Badge>
                {importResult.ignores > 0 && <Badge>{importResult.ignores} ignoré(s)</Badge>}
                {importResult.erreurs > 0 && <Badge>{importResult.erreurs} erreur(s)</Badge>}
              </div>
              <ul className="max-h-40 space-y-1 overflow-y-auto text-xs">
                {importResult.details.map((d) => (
                  <li key={d.ligne} className={d.statut === 'erreur' ? 'text-danger' : d.statut === 'ignore' ? 'text-ink-subtle' : 'text-success'}>
                    Ligne {d.ligne} — {d.enfant ? `${d.enfant} : ` : ''}{d.message}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Dialog>

      {/* Dialog détail famille : liste enfants + inscription */}
      <Dialog open={!!detail} onClose={() => { setDetail(null); setInscEnfant(null); }} title={detail ? `Famille ${detail.nom}` : ''} size="lg"
        footer={<Button variant="ghost" onClick={() => { setDetail(null); setInscEnfant(null); }}>Fermer</Button>}>
        {detailLoading ? (
          <p className="text-sm text-ink-muted">Chargement…</p>
        ) : detail && (
          <div className="space-y-4">
            <DossierFamilleStaff familleId={detail.id} nbEnfants={detail.enfants.length} />
            {/* Items 12.1/12.2 — les deux parents, et la suspension d'un parent. */}
            <FamilleResponsablesPanel
              familleId={detail.id}
              responsables={detail.responsables ?? []}
              estEncadrement={user?.role === 'manager' || user?.role === 'admin' || user?.role === 'super_admin'}
              onChange={() => { void (async () => {
                try { setDetail(await api.scolaire.getFamille(detail.id)); } catch { /* la liste reste affichée */ }
              })(); }}
            />
            <details className="rounded-r-lg border border-border bg-surface/30 p-3">
              <summary className="cursor-pointer text-sm font-medium text-ink">Pièces de la famille (bibliothèque)</summary>
              <div className="mt-3">
                <FamillePiecesPanel familleId={detail.id} accent="cantine" canValidate setError={setError} />
              </div>
            </details>
            {detail.enfants.length === 0 ? (
              <p className="text-sm text-ink-muted">Aucun enfant dans cette famille.</p>
            ) : (
              <ul className="space-y-2">
                {detail.enfants.map((enf) => (
                  <li key={enf.id} className="rounded-r-lg border border-border bg-surface/40 p-3">
                    <div className="flex items-center justify-between">
                      <div>
                        <span className="text-sm font-medium text-ink">{enf.prenom} {enf.nom}</span>
                        {enf.allergenes.length > 0 && <Badge className="ml-2">Allergie</Badge>}
                      </div>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setPresencesEnfant((id) => id === enf.id ? null : enf.id)}>
                          <CalendarDays className="h-4 w-4" aria-hidden="true" /> Présences
                        </Button>
                        <Button variant="cantine" size="sm" onClick={() => ouvrirInscription(enf)}>
                          <CalendarPlus className="h-4 w-4" aria-hidden="true" /> Inscrire
                        </Button>
                      </div>
                    </div>

                    {/* Gestion des présences au jour le jour — staff édite même après cutoff */}
                    {presencesEnfant === enf.id && (
                      <div className="mt-3 border-t border-border pt-3">
                        <PresenceCalendar enfantId={enf.id} forceEditable />
                      </div>
                    )}

                    {/* Formulaire d'inscription inline pour cet enfant */}
                    {inscEnfant?.id === enf.id && (
                      <div className="mt-3 space-y-3 border-t border-border pt-3">
                        <div className="flex gap-1.5">
                          {(['cantine', 'periscolaire', 'alsh'] as const).map((s) => (
                            <button key={s} type="button" onClick={() => setInscService(s)}
                              className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                                inscService === s ? 'bg-cantine text-white shadow-soft' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                              {s === 'cantine' ? 'Cantine' : s === 'periscolaire' ? 'Périscolaire' : 'ALSH'}
                            </button>
                          ))}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                          <Field label="Du" htmlFor={`d-${enf.id}`} required>
                            <Input id={`d-${enf.id}`} type="date" value={inscDebut} onChange={(e) => setInscDebut(e.target.value)} />
                          </Field>
                          <Field label="Au" htmlFor={`f-${enf.id}`} required>
                            <Input id={`f-${enf.id}`} type="date" value={inscFin} onChange={(e) => setInscFin(e.target.value)} />
                          </Field>
                        </div>
                        {inscService !== 'alsh' && (
                          <div>
                            <span className="mb-1.5 block text-sm font-medium text-ink">Jours</span>
                            <div className="flex flex-wrap gap-1.5">
                              {JOURS.map((j) => {
                                const on = inscJours.includes(j.n);
                                return (
                                  <button key={j.n} type="button"
                                    onClick={() => setInscJours((prev) => on ? prev.filter((x) => x !== j.n) : [...prev, j.n])}
                                    className={['inline-flex h-8 w-12 items-center justify-center rounded-r-md text-xs font-medium transition-all',
                                      on ? 'bg-cantine text-white' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                                    {j.label}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {inscService === 'alsh' && (
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <span className="mb-1.5 block text-sm font-medium text-ink">Période</span>
                              <div className="flex gap-1.5">
                                {([['mercredis', 'Mercredis'], ['vacances', 'Vacances']] as const).map(([v, label]) => (
                                  <button key={v} type="button" onClick={() => setInscAlshType(v)}
                                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                                      inscAlshType === v ? 'bg-cantine text-white' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <div>
                              <span className="mb-1.5 block text-sm font-medium text-ink">Formule</span>
                              <div className="flex flex-wrap gap-1.5">
                                {([['journee', 'Journée'], ['matin', 'Matin'], ['apres_midi', 'Après-midi']] as const).map(([v, label]) => (
                                  <button key={v} type="button" onClick={() => setInscAlshFormule(v)}
                                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium transition-all',
                                      inscAlshFormule === v ? 'bg-cantine text-white' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                                    {label}
                                  </button>
                                ))}
                              </div>
                            </div>
                            <p className="col-span-2 text-2xs text-ink-subtle">
                              Les réservations sont créées sur les jours d'accueil ALSH ouverts de la période (calendrier ALSH). « Mercredis » ne garde que les mercredis ouverts.
                            </p>
                          </div>
                        )}
                        {inscService === 'periscolaire' && (
                          <div>
                            <span className="mb-1.5 block text-sm font-medium text-ink">Créneaux</span>
                            <div className="flex gap-1.5">
                              {(['matin', 'soir'] as PeriscolaireCreneau[]).map((c) => {
                                const on = inscCreneaux.includes(c);
                                return (
                                  <button key={c} type="button"
                                    onClick={() => setInscCreneaux((prev) => on ? prev.filter((x) => x !== c) : [...prev, c])}
                                    className={['inline-flex h-8 items-center rounded-r-pill px-3 text-xs font-medium capitalize transition-all',
                                      on ? 'bg-cantine text-white' : 'border border-border text-ink-muted hover:bg-surface/60'].join(' ')}>
                                    {c}
                                  </button>
                                );
                              })}
                            </div>
                          </div>
                        )}
                        {inscMsg && <p className="text-xs text-cantine-fg">{inscMsg}</p>}
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setInscEnfant(null)}>Annuler</Button>
                          <Button variant="cantine" size="sm" loading={inscSaving} onClick={inscrire}>Valider l'inscription</Button>
                        </div>
                      </div>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </Dialog>
    </ScolaireLayout>
  );
}

// ============================================================================
/**
 * Encart dossier d'inscription côté STAFF (agent d'accueil) — pour aider une
 * famille non connectée : voir/créer son dossier, déposer les pièces, soumettre
 * à sa place. La validation reste réservée au valideur (GRC), pas ici.
 */
function DossierFamilleStaff({ familleId, nbEnfants }: { familleId: string; nbEnfants: number }) {
  const [dossier, setDossier] = useState<DossierScolaireDetail | null>(null);
  const [demandees, setDemandees] = useState<DossierPieceDemandee[]>([]);
  const [deposees, setDeposees] = useState<DossierPieceDeposee[]>([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [uploadingFor, setUploadingFor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function charger() {
    setOpen(true); setLoading(true); setError(null);
    try {
      const d = await api.scolaire.dossiers.getMien(familleId);
      const [dem, dep] = await Promise.all([
        api.scolaire.dossiers.listPiecesDemandees(),
        api.scolaire.dossiers.listPieces(d.id),
      ]);
      setDossier(d); setDemandees(dem); setDeposees(dep);
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setLoading(false); }
  }
  async function reload() {
    if (!dossier) return;
    const [d, dep] = await Promise.all([api.scolaire.dossiers.getMien(familleId), api.scolaire.dossiers.listPieces(dossier.id)]);
    setDossier(d); setDeposees(dep);
  }
  async function upload(pieceDemandeeId: string, file: File) {
    if (!dossier) return;
    setUploadingFor(pieceDemandeeId); setError(null);
    try { const f = await compressImageIfNeeded(file); await api.scolaire.dossiers.deposerPiece(dossier.id, f, pieceDemandeeId); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur lors du dépôt'); }
    finally { setUploadingFor(null); }
  }
  async function soumettre() {
    setBusy(true); setError(null);
    try { await api.scolaire.dossiers.soumettre(familleId); await reload(); }
    catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }

  if (!open) {
    return (
      <Card className="p-3">
        <div className="flex items-center justify-between gap-3">
          <span className="flex items-center gap-2 text-sm text-ink-muted"><FileText className="h-4 w-4" /> Dossier d'inscription</span>
          <Button variant="secondary" size="sm" onClick={charger}>Gérer le dossier</Button>
        </div>
      </Card>
    );
  }

  const statutLabel: Record<string, string> = { brouillon: 'Non soumis', soumis: 'En cours de validation', valide: 'Validé', refuse: 'À corriger' };
  const modifiable = dossier?.statut === 'brouillon' || dossier?.statut === 'refuse';
  const deposeePour = (demId: string) => deposees.find((p) => p.pieceDemandeeId === demId);

  return (
    <Card className="p-3">
      {error && <Alert variant="error" className="mb-2">{error}</Alert>}
      {loading ? (
        <p className="text-sm text-ink-muted">Chargement…</p>
      ) : dossier && (
        <div className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <span className="flex items-center gap-2 text-sm font-medium text-ink"><FileText className="h-4 w-4" /> Dossier — {statutLabel[dossier.statut]}</span>
            {modifiable && (
              <Button variant="primary" size="sm" loading={busy} onClick={soumettre} disabled={nbEnfants === 0}>
                Soumettre pour la famille
              </Button>
            )}
          </div>
          {dossier.motifRefus && <Alert variant="warning">Motif de refus : {dossier.motifRefus}</Alert>}
          {demandees.length === 0 ? (
            <p className="text-xs text-ink-subtle">Aucune pièce configurée pour la commune (voir « Dossier type » dans l'espace GRC).</p>
          ) : (
            <ul className="space-y-1.5">
              {demandees.map((dem) => {
                const dep = deposeePour(dem.id);
                return (
                  <li key={dem.id} className="flex items-center justify-between gap-2 rounded-r-md border border-border bg-surface/40 px-3 py-2">
                    <div className="min-w-0">
                      <span className="text-sm text-ink">{dem.libelle}</span>
                      {dem.obligatoire && <span className="ml-1 text-xs text-ink-subtle">(obligatoire)</span>}
                      {dep && <div className="text-xs text-success">✓ {dep.nomFichier}</div>}
                    </div>
                    {modifiable && (
                      <label className="shrink-0 cursor-pointer rounded-r-pill border border-border px-3 py-1.5 text-xs font-medium text-ink-muted hover:bg-surface/60">
                        {uploadingFor === dem.id ? 'Envoi…' : dep ? 'Remplacer' : 'Déposer'}
                        <input type="file" accept="image/*,application/pdf" className="hidden"
                          onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(dem.id, f); e.target.value = ''; }} />
                      </label>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
          {nbEnfants === 0 && <p className="text-xs text-ink-subtle">Ajoutez au moins un enfant à la famille avant de soumettre.</p>}
        </div>
      )}
    </Card>
  );
}
